import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { fmtArgent } from '@/lib/lmdsDeductions';
import { usePermissions } from '@/lib/PermissionsContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ETATS_ATTENTE = ['a_venir', 'en_attente', 'reportee'];
const BADGES = {
  appliquee: ['Appliquée', 'bg-emerald-100 text-emerald-800 border-emerald-300'],
  a_venir: ['À venir', 'bg-sky-100 text-sky-800 border-sky-300'],
  en_attente: ['En attente', 'bg-amber-100 text-amber-800 border-amber-300'],
  reportee: ['Reportée', 'bg-violet-100 text-violet-800 border-violet-300'],
};

function Ligne({ l }) {
  const [libelle, classes] = BADGES[l.etat] || [l.etat, ''];
  return (
    <li className="flex flex-col gap-0.5 border-b last:border-0 pb-1.5" data-testid={`detail-ligne-${l.occurrence_id}`}>
      <div className="flex justify-between gap-2">
        <span className="font-medium">{l.description} <span className="text-slate-500 font-normal">· {l.frequence}</span></span>
        <b>{fmtArgent(l.montant_total)}</b>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <Badge variant="outline" className={classes} data-testid={`detail-ligne-${l.occurrence_id}-etat`}>{libelle}</Badge>
        <span data-testid={`detail-ligne-${l.occurrence_id}-explication`}>{l.etat_libelle}</span>
        <span className="text-slate-400">{fmtArgent(l.montant_ht)} HT + TPS {fmtArgent(l.tps)} + TVQ {fmtArgent(l.tvq)}</span>
        {l.gele && <span className="text-slate-500">· mois payé</span>}
      </div>
    </li>
  );
}

// 🧾 Phase 2 (correctif) : « Détail des déductions » — manuel, automatiques appliquées, et à venir / en attente / reportées avec statut.
export function DetailDeductionsDialog({ ouvert, onFermer, installateurId, annee, semaine }) {
  const [detail, setDetail] = useState(null);
  const [erreur, setErreur] = useState('');
  const { canSeePage } = usePermissions() || {};
  useEffect(() => {
    if (!ouvert) return;
    setDetail(null); setErreur('');
    axios.get(`${API}/api/lmds/rapports/${installateurId}/${annee}/${semaine}/deductions-detail`)
      .then((r) => setDetail(r.data)).catch(() => setErreur('Impossible de charger le détail des déductions.'));
  }, [ouvert, installateurId, annee, semaine]);

  const appliquees = detail?.lignes.filter((l) => l.etat === 'appliquee') || [];
  const attente = detail?.lignes.filter((l) => ETATS_ATTENTE.includes(l.etat)) || [];
  const peutGerer = typeof canSeePage === 'function' && canSeePage('gestion_installateurs');

  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onFermer()}>
      <DialogContent className="max-w-xl" data-testid="dialog-detail-deductions">
        <DialogHeader>
          <DialogTitle>Détail des déductions — semaine {semaine} ({annee})</DialogTitle>
          <DialogDescription>Déduction manuelle du tableau, déductions automatiques appliquées, et celles prévues ou en attente pour cette semaine.</DialogDescription>
        </DialogHeader>
        {erreur && <p className="text-sm text-red-600" data-testid="detail-deductions-erreur">{erreur}</p>}
        {detail && (
          <div className="space-y-4 text-sm">
            <section data-testid="section-manuelle">
              <h4 className="font-semibold text-slate-700 mb-1">Déduction manuelle</h4>
              <div className="flex justify-between" data-testid="detail-manuel"><span>Saisie dans le tableau (hors taxes)</span><b>{fmtArgent(detail.deduction_manuelle)}</b></div>
            </section>
            <section data-testid="section-appliquees">
              <h4 className="font-semibold text-slate-700 mb-1">Déductions automatiques appliquées</h4>
              <ul className="space-y-1.5" data-testid="detail-lignes-appliquees">
                {appliquees.map((l) => <Ligne key={l.occurrence_id} l={l} />)}
                {!appliquees.length && <li className="text-slate-500" data-testid="detail-aucune-appliquee">Aucune déduction automatique prélevée cette semaine.</li>}
              </ul>
            </section>
            <section data-testid="section-attente">
              <h4 className="font-semibold text-slate-700 mb-1">À venir / en attente / reportées</h4>
              <ul className="space-y-1.5" data-testid="detail-lignes-attente">
                {attente.map((l) => <Ligne key={l.occurrence_id} l={l} />)}
                {!attente.length && <li className="text-slate-500" data-testid="detail-aucune-attente">Rien de prévu ni en attente pour cette semaine.</li>}
              </ul>
            </section>
            <div className="flex justify-between border-t-2 border-slate-800 pt-2 text-base" data-testid="detail-total-auto">
              <span className="font-semibold">Total réellement prélevé cette semaine (automatique, taxes incluses)</span><b data-testid="detail-total-auto-montant">{fmtArgent(detail.total_auto)}</b>
            </div>
            {detail.gele && <p className="text-xs text-slate-500" data-testid="detail-gele">Semaine payée : les déductions appliquées sont gelées.</p>}
            {peutGerer && (
              <a href="/sous-traitants/gestion-installateurs" className="text-xs text-blue-700 underline" data-testid="detail-lien-gestion">Gérer dans Gestion des installateurs</a>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
